/** Khoze chat thread reconciliation — the server owns the conversation order;
 *  the local list (optimistic bubbles + engine stream) can transiently race it
 *  and show an answer above its question. Merging heals that. */

export interface LocalMessage { id: string; role: "user" | "assistant"; text: string; sources?: unknown[] }
export interface ServerMessage { id: string; role: string; content: string; citations?: unknown }

export interface NormalizedMessage { id: string; role: "user" | "assistant"; text: string; sources: unknown[] }

/** Map a server row to the client shape. */
export function fromServer(message: ServerMessage): NormalizedMessage {
  return {
    id: message.id,
    role: message.role === "user" ? "user" : "assistant",
    text: message.content,
    sources: Array.isArray(message.citations) ? message.citations : []
  };
}

/**
 * Server-authoritative merge: server rows win and define the order; local-only
 * messages (an optimistic user bubble not yet persisted) survive, deduped by id
 * or by role+text so the optimistic twin of a persisted row never doubles up.
 */
export function mergeThreadMessages(local: LocalMessage[], server: ServerMessage[]): NormalizedMessage[] {
  const merged: NormalizedMessage[] = server.map(fromServer);
  const seenIds = new Set(merged.map((message) => message.id));
  const seenPairs = new Set(merged.map((message) => `${message.role}::${message.text}`));
  for (const message of local) {
    const pair = `${message.role}::${message.text}`;
    if (seenIds.has(message.id) || seenPairs.has(pair)) continue;
    seenIds.add(message.id);
    seenPairs.add(pair);
    merged.push({ id: message.id, role: message.role, text: message.text, sources: Array.isArray(message.sources) ? message.sources : [] });
  }
  return merged;
}
